import { useEffect, useMemo, useState, useCallback } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Chip,
  Button,
  TextField,
  CircularProgress,
  Switch,
  FormControlLabel,
  TablePagination,
  Tooltip,
  Stack,
} from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import api from "../api/api";
import Papa from "papaparse";
import { saveAs } from "file-saver";
import { Bar, Line, Doughnut } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  ArcElement,
  PointElement,
  Title,
  Tooltip as CTooltip,
  Legend,
} from "chart.js";
import { useTheme } from "@mui/material/styles";
import { useMediaQuery } from "@mui/material";
import * as XLSX from "xlsx";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  ArcElement,
  PointElement,
  Title,
  CTooltip,
  Legend
);

// ===================== PARAMETROS =====================
// Faixa OK: Bom/Boa/Ideal/Confortável.
// Para VOC/NOx index, limite = meta do “Boa” (100) p/ Δ intuitivo.
const PARAMS_BASE = [
  {
    key: "CO2",
    label: "CO₂",
    cor: "#43a047",
    unidade: "ppm",
    limite: 1000,
    fonte: "ABNT NBR 17037:2023 / triagem sem externo",
    faixa: (v) =>
      v <= 1000
        ? { rotulo: "Bom", cor: "#43a047" }
        : v <= 1200
        ? { rotulo: "Atenção", cor: "#fbc02d" }
        : { rotulo: "Ruim", cor: "#d32f2f" },
  },
  {
    key: "PM2_5",
    label: "MP2.5",
    cor: "#ffa726",
    unidade: "µg/m³",
    limite: 35,
    fonte: "IQAr/MMA jan-2025 / Conama 506:2024",
    faixa: (v) =>
      v <= 12
        ? { rotulo: "Boa", cor: "#43a047" }
        : v <= 35
        ? { rotulo: "Moderada", cor: "#ffa726" }
        : v <= 55
        ? { rotulo: "Ruim", cor: "#ff9800" }
        : { rotulo: "Muito ruim", cor: "#d32f2f" },
  },
  {
    key: "PM10",
    label: "MP10",
    cor: "#f44336",
    unidade: "µg/m³",
    limite: 50,
    fonte: "IQAr/MMA jan-2025 / Conama 506:2024",
    faixa: (v) =>
      v <= 20
        ? { rotulo: "Boa", cor: "#43a047" }
        : v <= 50
        ? { rotulo: "Moderada", cor: "#ffa726" }
        : v <= 100
        ? { rotulo: "Ruim", cor: "#ff9800" }
        : v <= 150
        ? { rotulo: "Muito ruim", cor: "#d32f2f" }
        : { rotulo: "Péssima", cor: "#b71c1c" },
  },
  {
    key: "PM1",
    label: "MP1",
    cor: "#ab47bc",
    unidade: "µg/m³",
    limite: 35,
    fonte: "OMS/PM2.5 (proxy)",
    faixa: (v) =>
      v <= 12
        ? { rotulo: "Boa", cor: "#43a047" }
        : v <= 35
        ? { rotulo: "Moderada", cor: "#ffa726" }
        : v <= 55
        ? { rotulo: "Ruim", cor: "#ff9800" }
        : { rotulo: "Muito ruim", cor: "#d32f2f" },
  },

  // TEMP/UMID faixa
  {
    key: "Temp",
    label: "Temperatura",
    cor: "#039be5",
    unidade: "°C",
    min: 21,
    max: 26,
    fonte: "ABNT NBR 17037:2023",
    faixa: (v) =>
      v < 18
        ? { rotulo: "Frio", cor: "#1976d2" }
        : v < 24
        ? { rotulo: "Confortável", cor: "#43a047" }
        : v < 27
        ? { rotulo: "Calor", cor: "#ffa726" }
        : { rotulo: "Calor excessivo", cor: "#d32f2f" },
  },
  {
    key: "Umid",
    label: "Umidade",
    cor: "#00bcd4",
    unidade: "%",
    min: 35,
    max: 65,
    fonte: "ABNT NBR 17037:2023",
    faixa: (v) =>
      v < 30
        ? { rotulo: "Muito seco", cor: "#1976d2" }
        : v < 40
        ? { rotulo: "Seco", cor: "#fbc02d" }
        : v <= 70
        ? { rotulo: "Ideal", cor: "#43a047" }
        : v <= 80
        ? { rotulo: "Úmido", cor: "#ffa726" }
        : { rotulo: "Muito úmido", cor: "#d32f2f" },
  },

  // MED
  {
    key: "VOC_ppm",
    label: "VOC",
    cor: "#e91e63",
    unidade: "ppm",
    limite: 5,
    fonte: "Leitura indicativa / triagem técnica",
    faixa: (v) =>
      v <= 5
        ? { rotulo: "Boa", cor: "#43a047" }
        : v <= 20
        ? { rotulo: "Atenção", cor: "#fbc02d" }
        : { rotulo: "Ruim", cor: "#d32f2f" },
  },
  {
    key: "HCHO",
    label: "Formaldeído",
    cor: "#26a69a",
    unidade: "mg/m³",
    limite: 0.1,
    fonte: "Leitura indicativa / triagem técnica",
    faixa: (v) =>
      v <= 0.1
        ? { rotulo: "Boa", cor: "#43a047" }
        : v <= 0.2
        ? { rotulo: "Atenção", cor: "#fbc02d" }
        : { rotulo: "Ruim", cor: "#d32f2f" },
  },

  // Habitat
  {
    key: "VOC",
    label: "VOC",
    cor: "#e91e63",
    unidade: "index",
    limite: 100,
    fonte: "Indicador relativo (sem equivalência legal direta)",
    faixa: (v) =>
      v <= 100
        ? { rotulo: "Boa", cor: "#43a047" }
        : v <= 200
        ? { rotulo: "Moderada", cor: "#ffa726" }
        : v <= 300
        ? { rotulo: "Ruim", cor: "#ff9800" }
        : v <= 400
        ? { rotulo: "Muito ruim", cor: "#d32f2f" }
        : { rotulo: "Péssima", cor: "#b71c1c" },
  },
  {
    key: "NOx",
    label: "NOx",
    cor: "#ffeb3b",
    unidade: "index",
    limite: 100,
    fonte: "Indicador relativo (sem equivalência legal direta)",
    faixa: (v) =>
      v <= 100
        ? { rotulo: "Boa", cor: "#43a047" }
        : v <= 200
        ? { rotulo: "Moderada", cor: "#ffa726" }
        : v <= 300
        ? { rotulo: "Ruim", cor: "#ff9800" }
        : v <= 400
        ? { rotulo: "Muito ruim", cor: "#d32f2f" }
        : { rotulo: "Péssima", cor: "#b71c1c" },
  },

  // Pro (se aparecerem no período)
  {
    key: "CO",
    label: "CO",
    cor: "#fdd835",
    unidade: "ppm",
    limite: 9,
    fonte: "IQAr/MMA jan-2025 / Conama 506:2024",
    faixa: (v) =>
      v <= 9
        ? { rotulo: "Boa", cor: "#43a047" }
        : v <= 11
        ? { rotulo: "Moderada", cor: "#ffa726" }
        : v <= 13
        ? { rotulo: "Ruim", cor: "#ff9800" }
        : v <= 15
        ? { rotulo: "Muito ruim", cor: "#d32f2f" }
        : { rotulo: "Péssima", cor: "#b71c1c" },
  },
  {
    key: "H2S",
    label: "H₂S",
    cor: "#8d6e63",
    unidade: "ppm",
    limite: 8,
    fonte: "NR-15 Anexo 11",
    faixa: (v) =>
      v <= 5
        ? { rotulo: "Boa", cor: "#43a047" }
        : v <= 8
        ? { rotulo: "Atenção", cor: "#fbc02d" }
        : { rotulo: "Acima NR-15", cor: "#d32f2f" },
  },
];

// ===================== NORMALIZAÇÃO =====================
function normSensor(raw) {
  const s = (raw || "")
    .toString()
    .trim()
    .replace(/\./g, "_")
    .replace(/\s+/g, "")
    .toUpperCase();

  if (s === "PM2_5") return "PM2_5";
  if (s === "VOC_PPM") return "VOC_ppm";
  if (s === "NOX") return "NOx";
  if (s === "TEMP") return "Temp";
  if (s === "UMID") return "Umid";
  if (s === "PRESSAO") return "pressao";

  // Sense Pro: o histórico pode chegar como CO_PPM / H2S_PPM
  if (s === "CO" || s === "CO_PPM") return "CO";
  if (s === "H2S" || s === "H2S_PPM") return "H2S";

  return s;
}
function findParamByKey(key) {
  return PARAMS_BASE.find((p) => p.key === key) || null;
}

// ===================== DATA/HORA =====================
function parseToDate(ts) {
  if (!ts) return null;
  if (ts instanceof Date) return isNaN(ts.getTime()) ? null : ts;
  const d = new Date(ts);
  return isNaN(d.getTime()) ? null : d;
}
function fmtLocal(dt) {
  if (!dt) return "-";
  const dia = dt.getDate().toString().padStart(2, "0");
  const mes = (dt.getMonth() + 1).toString().padStart(2, "0");
  const hora = dt.getHours().toString().padStart(2, "0");
  const min = dt.getMinutes().toString().padStart(2, "0");
  return `${dia}/${mes} ${hora}:${min}`;
}
function hourBucket(dt) {
  if (!dt) return "-";
  const dia = dt.getDate().toString().padStart(2, "0");
  const mes = (dt.getMonth() + 1).toString().padStart(2, "0");
  const hora = dt.getHours().toString().padStart(2, "0");
  return `${dia}/${mes} ${hora}:00`;
}

// Backend espera UTC no formato SQL "YYYY-MM-DD HH:mm:ss"
const formatDateUTCForSQL = (date) => {
  if (!date) return "";
  return date.toISOString().slice(0, 19).replace("T", " ");
};

// “OK” (não-alarme)
function isFaixaOk(rotulo) {
  return ["Bom", "Boa", "Ideal", "Confortável", "Recomendável"].includes(rotulo);
}
function severityRank(rotulo) {
  const r = String(rotulo || "").toLowerCase();
  if (r.includes("péss")) return 6;
  if (r.includes("muito")) return 5;
  if (r.includes("ruim")) return 4;
  if (r.includes("aten")) return 3;
  if (r.includes("moder")) return 2;
  return 1;
}

// ===================== Δ helpers =====================
function deltaPctAgainstLimit(value, limit) {
  if (!Number.isFinite(value) || !Number.isFinite(limit) || limit === 0) return null;
  return ((value - limit) / limit) * 100;
}
function deltaPctAgainstRange(value, min, max) {
  if (!Number.isFinite(value) || (!Number.isFinite(min) && !Number.isFinite(max))) return null;

  if (Number.isFinite(min) && value < min) return min !== 0 ? ((value - min) / min) * 100 : null;
  if (Number.isFinite(max) && value > max) return max !== 0 ? ((value - max) / max) * 100 : null;

  // dentro: “folga” vs limite mais próximo (preferindo max)
  if (Number.isFinite(max) && max !== 0) return ((value - max) / max) * 100;
  if (Number.isFinite(min) && min !== 0) return ((value - min) / min) * 100;
  return null;
}
function limitLabel(param) {
  if (param?.key === "CO") return "IQAr CO 8h: boa ≤9; mod. ≤11; ruim ≤13; m.ruim ≤15";
  if (param?.key === "H2S") return "NR-15: LT 8 ppm";
  if (param?.key === "VOC") return "Índice relativo de VOC";
  if (param?.key === "NOx") return "Índice relativo de NOx";
  if (param?.key === "VOC_ppm") return "Leitura indicativa de COV";
  if (param?.key === "HCHO") return "Leitura indicativa de formaldeído";
  if (Number.isFinite(param?.min) && Number.isFinite(param?.max)) {
    return `${param.min}–${param.max} ${param.unidade || ""}`.trim();
  }
  if (param?.limite != null && !isNaN(param.limite)) {
    return `${param.limite} ${param.unidade || ""}`.trim();
  }
  return "-";
}

// ===================== Formatação BR =====================
const brNum = (n, dec = 2) => (Number.isFinite(n) ? Number(n).toFixed(dec).replace(".", ",") : "-");

// CSV “Excel/LibreOffice BR”
function downloadCsvExcelBR(rows, filename) {
  const csvBody = Papa.unparse(rows, {
    delimiter: ";",
    quotes: false,
    newline: "\r\n",
  });

  const prefix = "sep=;\r\n";
  const csvWithBom = "\uFEFF" + prefix + csvBody;

  const blob = new Blob([csvWithBom], { type: "text/csv;charset=utf-8;" });
  saveAs(blob, filename);
}

// XLSX (abre direto no LibreOffice)
function downloadXlsx(rows, filename, sheetName = "Ocorrências") {
  const ws = XLSX.utils.json_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName);
  const out = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  const blob = new Blob([out], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  saveAs(blob, filename);
}

// ===================== Indicador por parâmetro =====================
// score 0..100 a partir de taxa de alarmes e severidade média
function scoreFromRate(rate01, avgSev) {
  if (!Number.isFinite(rate01)) return null;
  const sev = Math.min(6, Math.max(1, Number(avgSev || 1)));
  const sevFactor = 0.7 + (sev - 1) * 0.15; // ~0.7..1.45
  const x = rate01 * 100 * sevFactor;
  const score = 100 * Math.exp(-0.12 * x);
  return Math.max(0, Math.min(100, score));
}

function labelFromScore(score) {
  if (score == null) return { rotulo: "Sem dados", cor: "#9e9e9e" };
  if (score >= 90) return { rotulo: "Excelente", cor: "#2e7d32" };
  if (score >= 75) return { rotulo: "Bom", cor: "#43a047" };
  if (score >= 55) return { rotulo: "Atenção", cor: "#fbc02d" };
  if (score >= 35) return { rotulo: "Ruim", cor: "#ff9800" };
  return { rotulo: "Crítico", cor: "#d32f2f" };
}

export default function Alarmes() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [equipamentos, setEquipamentos] = useState([]);
  const [mac, setMac] = useState("");

  // padrão 24h
  const [dataIni, setDataIni] = useState(() => {
    const d = new Date();
    d.setHours(d.getHours() - 24);
    return d;
  });
  const [dataFim, setDataFim] = useState(() => new Date());

  const [paramFiltro, setParamFiltro] = useState("");
  const [search, setSearch] = useState("");

  const [alarmList, setAlarmList] = useState([]);
  const [erro, setErro] = useState("");
  const [loading, setLoading] = useState(false);

  const [parametrosAtivos, setParametrosAtivos] = useState(PARAMS_BASE);
  const [groupByHour, setGroupByHour] = useState(true);

  const [modoGrafico, setModoGrafico] = useState("rate"); // rate|count|impact

  // paginação
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(isMobile ? 10 : 15);

  // totais do período (para taxas por parâmetro)
  const [totalByKey, setTotalByKey] = useState({}); // { CO2: 1000, VOC: 800, ... }
  const [totalRegistrosPeriodo, setTotalRegistrosPeriodo] = useState(0);

  // buscar ambientes
  useEffect(() => {
    api
      .get("/equipment")
      .then((res) => {
        const list = res.data || [];
        setEquipamentos(list);
        setMac((prev) => prev || (list.length ? list[0].mac : ""));
      })
      .catch(() => {});
  }, []);

  const fetchAlarmes = useCallback(async () => {
    if (!mac || !dataIni || !dataFim) return;

    if (dataFim < dataIni) {
      setErro("Período inválido: data final antes da inicial.");
      return;
    }

    setLoading(true);
    setErro("");
    setPage(0);

    try {
      const startStr = formatDateUTCForSQL(dataIni);
      const endStr = formatDateUTCForSQL(dataFim);

      const res = await api.get("/historic-data", {
        params: { mac, start_date: startStr, end_date: endStr },
      });

      const rows = Array.isArray(res.data) ? res.data : [];
      setTotalRegistrosPeriodo(rows.length);

      // 1) sensores presentes no período
      const presentes = new Set(rows.map((r) => normSensor(r.sensor)));

      // 2) MED se tem HCHO
      const isMED = presentes.has("HCHO");

      // 3) VOC_ppm ativo se existe VOC_ppm ou (MED e existir VOC legado)
      const hasVocPpm = presentes.has("VOC_ppm");
      const hasVoc = presentes.has("VOC");
      const vocPpmAtivo = hasVocPpm || (isMED && hasVoc);

      // 4) ativa só o que existe
      const ativos = PARAMS_BASE.filter((p) => {
        if (isMED && (p.key === "VOC" || p.key === "NOx")) return false;
        if (p.key === "VOC_ppm") return vocPpmAtivo;
        if (p.key === "VOC") return !isMED && hasVoc && !hasVocPpm;
        if (p.key === "NOx") return !isMED && presentes.has("NOx");
        return presentes.has(p.key);
      });

      setParametrosAtivos(ativos);
      if (paramFiltro && !ativos.some((a) => a.key === paramFiltro)) setParamFiltro("");

      // totais por parâmetro (denominador da taxa)
      const totals = {};
      const ativosSet = new Set(ativos.map((a) => a.key));

      for (const reg of rows) {
        let sensorKey = normSensor(reg.sensor);

        // MED: VOC legado vira VOC_ppm; NOx ignorado
        if (isMED && sensorKey === "VOC") sensorKey = "VOC_ppm";
        if (isMED && sensorKey === "NOx") continue;

        // se VOC_ppm está ativo, não deixa VOC(index) entrar
        if (sensorKey === "VOC" && vocPpmAtivo) continue;

        if (!ativosSet.has(sensorKey)) continue;

        totals[sensorKey] = (totals[sensorKey] || 0) + 1;
      }
      setTotalByKey(totals);

      // lista de alarmes
      const alarms = [];
      for (const reg of rows) {
        let sensorKey = normSensor(reg.sensor);

        if (isMED && sensorKey === "VOC") sensorKey = "VOC_ppm";
        if (isMED && sensorKey === "NOx") continue;
        if (sensorKey === "VOC" && vocPpmAtivo) continue;

        if (!ativosSet.has(sensorKey)) continue;

        const param = findParamByKey(sensorKey);
        if (!param) continue;

        const valor = Number(reg.value);
        if (!Number.isFinite(valor)) continue;

        const faixa = param.faixa ? param.faixa(valor) : null;
        if (!faixa) continue;

        if (!isFaixaOk(faixa.rotulo)) {
          const dt = parseToDate(reg.timestamp);
          alarms.push({
            ...param,
            valor,
            ts: dt ? dt.getTime() : null,
            dt,
            horario: fmtLocal(dt),
            bucket: hourBucket(dt),
            faixa: faixa.rotulo,
            cor: faixa.cor,
          });
        }
      }

      alarms.sort((a, b) => (b.ts || 0) - (a.ts || 0));
      setAlarmList(alarms);
    } catch (e) {
      setErro("Falha ao buscar dados históricos.");
      setAlarmList([]);
      setParametrosAtivos(PARAMS_BASE);
      setTotalByKey({});
      setTotalRegistrosPeriodo(0);
    } finally {
      setLoading(false);
    }
  }, [mac, dataIni, dataFim, paramFiltro]);

  useEffect(() => {
    if (mac) fetchAlarmes();
  }, [mac, fetchAlarmes]);

  // filtros base
  const alarmListFiltrado = useMemo(() => {
    const q = (search || "").toLowerCase();
    return alarmList.filter((al) => {
      const okParam = !paramFiltro || al.key === paramFiltro;
      const okBusca =
        !q ||
        (al.label || "").toLowerCase().includes(q) ||
        (al.horario || "").toLowerCase().includes(q) ||
        (al.bucket || "").toLowerCase().includes(q) ||
        String(al.valor ?? "").toLowerCase().includes(q) ||
        String(al.faixa ?? "").toLowerCase().includes(q);
      return okParam && okBusca;
    });
  }, [alarmList, paramFiltro, search]);

  // modo resumido por hora
  const alarmHourly = useMemo(() => {
    const map = new Map(); // key: bucket|paramKey
    for (const al of alarmListFiltrado) {
      const k = `${al.bucket}__${al.key}`;
      const cur = map.get(k);
      if (!cur) {
        map.set(k, {
          bucket: al.bucket,
          key: al.key,
          label: al.label,
          unidade: al.unidade,
          limite: al.limite,
          minAllowed: al.min,
          maxAllowed: al.max,
          fonte: al.fonte,
          corBase: al.cor,
          count: 1,
          sum: al.valor,
          max: al.valor,
          worstFaixa: al.faixa,
          worstRank: severityRank(al.faixa),
        });
      } else {
        cur.count += 1;
        cur.sum += al.valor;
        if (al.valor > cur.max) cur.max = al.valor;
        const rk = severityRank(al.faixa);
        if (rk > cur.worstRank) {
          cur.worstRank = rk;
          cur.worstFaixa = al.faixa;
          cur.corBase = al.cor;
        }
      }
    }

    const arr = Array.from(map.values());
    arr.sort((a, b) => {
      if (a.bucket === b.bucket) return b.worstRank - a.worstRank;
      return a.bucket < b.bucket ? 1 : -1;
    });
    return arr;
  }, [alarmListFiltrado]);

  const tableRows = groupByHour ? alarmHourly : alarmListFiltrado;

  // paginação
  const pagedRows = useMemo(() => {
    const start = page * rowsPerPage;
    return tableRows.slice(start, start + rowsPerPage);
  }, [tableRows, page, rowsPerPage]);

  // ===================== Indicadores por parâmetro =====================
  const indicadorPorParametro = useMemo(() => {
    const ativos = parametrosAtivos || [];

    // alarmes do período (sem filtro de busca/pesquisa pra não distorcer indicador)
    // mas respeita filtro de parâmetro se usuário escolheu (pra ficar coerente)
    const base = paramFiltro ? alarmList.filter((a) => a.key === paramFiltro) : alarmList;

    const alarmsByKey = {};
    const sevSumByKey = {};
    for (const a of base) {
      alarmsByKey[a.key] = (alarmsByKey[a.key] || 0) + 1;
      sevSumByKey[a.key] = (sevSumByKey[a.key] || 0) + severityRank(a.faixa);
    }

    return ativos
      .filter((p) => !paramFiltro || p.key === paramFiltro)
      .map((p) => {
        const total = totalByKey[p.key] || 0;
        const alarms = alarmsByKey[p.key] || 0;
        const avgSev = alarms ? (sevSumByKey[p.key] || 0) / alarms : 0;
        const rate01 = total ? alarms / total : null;
        const score = scoreFromRate(rate01, avgSev);
        const meta = labelFromScore(score);

        return {
          key: p.key,
          label: p.label,
          cor: p.cor,
          total,
          alarms,
          ratePct: rate01 == null ? null : rate01 * 100,
          avgSev,
          score,
          status: meta.rotulo,
          statusColor: meta.cor,
        };
      })
      .sort((a, b) => {
        // pior primeiro (score menor)
        const as = a.score == null ? 999 : a.score;
        const bs = b.score == null ? 999 : b.score;
        return as - bs;
      });
  }, [parametrosAtivos, alarmList, totalByKey, paramFiltro]);

  // ===================== EXPORTAÇÃO =====================
  function buildExportRows() {
    if (!tableRows.length) return [];

    if (groupByHour) {
      return tableRows.map((r) => {
        const avg = r.count ? r.sum / r.count : 0;

        const hasRange = Number.isFinite(r.minAllowed) && Number.isFinite(r.maxAllowed);
        const dMax = hasRange
          ? deltaPctAgainstRange(r.max, r.minAllowed, r.maxAllowed)
          : deltaPctAgainstLimit(r.max, Number(r.limite));

        const dAvg = hasRange
          ? deltaPctAgainstRange(avg, r.minAllowed, r.maxAllowed)
          : deltaPctAgainstLimit(avg, Number(r.limite));

        return {
          Hora: r.bucket,
          "Parâmetro": r.label,
          "Nº ocorrências": r.count,
          "Máximo": r.max,
          "Média": Number(avg.toFixed(2)),
          "Unidade": r.unidade,
          "Pior faixa": r.worstFaixa,
          "Critério": limitLabel(r),
          "Δ pico (%)": dMax == null ? null : Number(dMax.toFixed(1)),
          "Δ média (%)": dAvg == null ? null : Number(dAvg.toFixed(1)),
          "Referência": r.fonte || "-",
        };
      });
    }

    return tableRows.map((al) => {
      const hasRange = Number.isFinite(al.min) && Number.isFinite(al.max);
      const d = hasRange
        ? deltaPctAgainstRange(Number(al.valor), al.min, al.max)
        : deltaPctAgainstLimit(Number(al.valor), Number(al.limite));

      return {
        "Horário": al.horario,
        "Parâmetro": al.label,
        "Faixa": al.faixa,
        "Valor": Number(Number(al.valor).toFixed(2)),
        "Unidade": al.unidade,
        "Critério": limitLabel(al),
        "Δ (%)": d == null ? null : Number(d.toFixed(1)),
        "Referência": al.fonte || "-",
      };
    });
  }

  function exportarCSV() {
    const rows = buildExportRows();
    if (!rows.length) return;

    // CSV BR: valores como string com vírgula (pra abrir bem em pt-BR)
    const rowsBR = rows.map((r) => {
      const out = {};
      for (const [k, v] of Object.entries(r)) {
        if (typeof v === "number") out[k] = brNum(v, k.includes("Δ") ? 1 : 2);
        else out[k] = v == null ? "-" : String(v);
      }
      return out;
    });

    downloadCsvExcelBR(rowsBR, `ocorrencias_${mac}_${new Date().toISOString().slice(0, 10)}.csv`);
  }

  function exportarXLSX() {
    const rows = buildExportRows();
    if (!rows.length) return;
    downloadXlsx(rows, `ocorrencias_${mac}_${new Date().toISOString().slice(0, 10)}.xlsx`, groupByHour ? "ResumoHora" : "Ocorrências");
  }

  // ===================== GRÁFICOS =====================
  const chartParams = parametrosAtivos;

  const countByKey = useMemo(() => {
    const map = new Map();
    if (groupByHour) {
      for (const r of alarmHourly) map.set(r.key, (map.get(r.key) || 0) + r.count);
    } else {
      for (const al of alarmListFiltrado) map.set(al.key, (map.get(al.key) || 0) + 1);
    }
    return map;
  }, [groupByHour, alarmHourly, alarmListFiltrado]);

  const impactByKey = useMemo(() => {
    const map = new Map();
    if (groupByHour) {
      for (const r of alarmHourly) map.set(r.key, (map.get(r.key) || 0) + r.count * (r.worstRank || 1));
    } else {
      for (const al of alarmListFiltrado) map.set(al.key, (map.get(al.key) || 0) + severityRank(al.faixa));
    }
    return map;
  }, [groupByHour, alarmHourly, alarmListFiltrado]);

  const rateByKey = useMemo(() => {
    const map = new Map();
    for (const p of chartParams) {
      const alarms = countByKey.get(p.key) || 0;
      const total = totalByKey[p.key] || 0;
      map.set(p.key, total ? (alarms / total) * 100 : 0);
    }
    return map;
  }, [chartParams, countByKey, totalByKey]);

  const barrasData = useMemo(() => {
    const getVal = (key) => {
      if (modoGrafico === "impact") return impactByKey.get(key) || 0;
      if (modoGrafico === "count") return countByKey.get(key) || 0;
      return rateByKey.get(key) || 0;
    };

    return {
      labels: chartParams.map((p) => p.label),
      datasets: [
        {
          label: modoGrafico === "impact" ? "Impacto" : modoGrafico === "count" ? "Quantidade" : "Taxa (%)",
          data: chartParams.map((p) => getVal(p.key)),
          backgroundColor: chartParams.map((p) => p.cor),
        },
      ],
    };
  }, [chartParams, modoGrafico, impactByKey, countByKey, rateByKey]);

  // Evolução: por dia (count/rate/impact)
  const lineData = useMemo(() => {
    const byDay = {}; // dd/mm -> value

    // base: se groupByHour, usa bucket (dd/mm hh:00)
    // senão, usa horario (dd/mm hh:mm)
    const base = groupByHour
      ? alarmHourly.map((r) => ({ when: r.bucket, key: r.key, count: r.count, impact: r.count * (r.worstRank || 1) }))
      : alarmListFiltrado.map((a) => ({ when: a.horario, key: a.key, count: 1, impact: severityRank(a.faixa) }));

    for (const it of base) {
      const s = it.when || "";
      const day = s.length >= 5 ? s.slice(0, 5) : "-";
      if (!byDay[day]) byDay[day] = { count: 0, impact: 0, alarmsByKey: {} };
      byDay[day].count += it.count || 0;
      byDay[day].impact += it.impact || 0;
      byDay[day].alarmsByKey[it.key] = (byDay[day].alarmsByKey[it.key] || 0) + (it.count || 0);
    }

    const days = Object.keys(byDay)
      .filter((d) => d !== "-")
      .sort((a, b) => {
        const [da, ma] = a.split("/").map(Number);
        const [db, mb] = b.split("/").map(Number);
        return ma === mb ? da - db : ma - mb;
      });

    const values = days.map((d) => {
      if (modoGrafico === "impact") return byDay[d].impact;

      if (modoGrafico === "count") return byDay[d].count;

      // rate: média ponderada da taxa diária por parâmetro (alarmes do dia / total do período por parâmetro)
      // (Sem “total por dia” do backend, esse é um proxy mais estável do que somar absoluto)
      let sumRates = 0;
      let used = 0;
      for (const p of chartParams) {
        const alarms = byDay[d].alarmsByKey[p.key] || 0;
        const total = totalByKey[p.key] || 0;
        if (total > 0) {
          sumRates += (alarms / total) * 100;
          used += 1;
        }
      }
      return used ? sumRates / used : 0;
    });

    return {
      labels: days,
      datasets: [
        {
          label: "Evolução",
          data: values,
          borderColor: "#d32f2f",
          backgroundColor: "#ffcdd2",
          tension: 0.25,
          fill: true,
          pointRadius: 2,
        },
      ],
    };
  }, [alarmHourly, alarmListFiltrado, groupByHour, modoGrafico, chartParams, totalByKey]);

  const pizzaData = useMemo(() => {
    const getVal = (key) => {
      if (modoGrafico === "impact") return impactByKey.get(key) || 0;
      if (modoGrafico === "count") return countByKey.get(key) || 0;
      return rateByKey.get(key) || 0;
    };

    return {
      labels: chartParams.map((p) => p.label),
      datasets: [
        {
          data: chartParams.map((p) => getVal(p.key)),
          backgroundColor: chartParams.map((p) => p.cor),
          borderWidth: 2,
        },
      ],
    };
  }, [chartParams, modoGrafico, impactByKey, countByKey, rateByKey]);

  return (
    <Box sx={{ maxWidth: 1400, mx: "auto", p: { xs: 1, sm: 2, md: 3 } }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Typography variant={isMobile ? "h6" : "h4"} gutterBottom>
          Parâmetros fora da conformidade
        </Typography>

        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          <FormControl sx={{ minWidth: 180 }}>
            <InputLabel>Gráficos</InputLabel>
            <Select
              value={modoGrafico}
              label="Gráficos"
              onChange={(e) => setModoGrafico(e.target.value)}
              size={isMobile ? "small" : "medium"}
            >
              <MenuItem value="rate">Taxa (%)</MenuItem>
              <MenuItem value="count">Quantidade</MenuItem>
              <MenuItem value="impact">Impacto</MenuItem>
            </Select>
          </FormControl>

          <FormControlLabel
            control={
              <Switch
                checked={groupByHour}
                onChange={(e) => {
                  setGroupByHour(e.target.checked);
                  setPage(0);
                }}
              />
            }
            label={groupByHour ? "Resumo por hora (recomendado)" : "Detalhado (evento a evento)"}
          />
        </Stack>
      </Box>

      <Paper
        sx={{
          p: { xs: 1.2, sm: 2 },
          mb: 2,
          display: "flex",
          flexWrap: "wrap",
          gap: { xs: 1, sm: 2 },
          alignItems: "center",
        }}
      >
        <FormControl sx={{ minWidth: 210 }}>
          <InputLabel>Ambiente</InputLabel>
          <Select
            value={mac}
            label="Ambiente"
            onChange={(e) => setMac(e.target.value)}
            size={isMobile ? "small" : "medium"}
          >
            {equipamentos.map((e) => (
              <MenuItem key={e.mac} value={e.mac}>
                {e.location || e.mac}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <DateTimePicker
          label="Data/Hora Inicial"
          value={dataIni}
          onChange={(v) => setDataIni(v)}
          sx={{ minWidth: 190 }}
          slotProps={{ textField: { size: isMobile ? "small" : "medium" } }}
        />
        <DateTimePicker
          label="Data/Hora Final"
          value={dataFim}
          onChange={(v) => setDataFim(v)}
          sx={{ minWidth: 190 }}
          slotProps={{ textField: { size: isMobile ? "small" : "medium" } }}
        />

        <FormControl sx={{ minWidth: 160 }}>
          <InputLabel>Parâmetro</InputLabel>
          <Select
            value={paramFiltro}
            label="Parâmetro"
            onChange={(e) => {
              setParamFiltro(e.target.value);
              setPage(0);
            }}
            size={isMobile ? "small" : "medium"}
          >
            <MenuItem value="">Todos</MenuItem>
            {parametrosAtivos.map((p) => (
              <MenuItem key={p.key} value={p.key}>
                {p.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          label="Buscar"
          placeholder={groupByHour ? "Hora, parâmetro, faixa..." : "Horário, valor..."}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(0);
          }}
          sx={{ minWidth: 220 }}
          size={isMobile ? "small" : "medium"}
        />

        <Button variant="outlined" onClick={fetchAlarmes} size={isMobile ? "small" : "medium"}>
          BUSCAR
        </Button>

        <Button variant="contained" onClick={exportarCSV} size={isMobile ? "small" : "medium"}>
          Exportar CSV
        </Button>

        <Button variant="contained" color="success" onClick={exportarXLSX} size={isMobile ? "small" : "medium"}>
          Exportar XLSX
        </Button>

        {loading && <CircularProgress size={22} sx={{ ml: 1 }} />}

        <Chip
          size="small"
          variant="outlined"
          label={
            totalRegistrosPeriodo
              ? `Registros no período: ${totalRegistrosPeriodo}`
              : "Sem registros no período"
          }
          sx={{ fontWeight: 700 }}
        />
      </Paper>

      <Alert severity="info" sx={{ mb: 2 }}>
        Referências da página: <b>ABNT NBR 17037:2023</b> para conforto/IAQ, <b>IQAr/MMA jan-2025 / Conama 506:2024</b> para CO e <b>NR-15 Anexo 11</b> para H₂S. VOC/NOx em índice, VOC em ppm e formaldeído são tratados como <b>indicadores técnicos</b>, sem equivalência legal direta universal no formato exibido pelo sensor.
      </Alert>

      {/* INDICADORES POR PARÂMETRO */}
      <Paper sx={{ p: { xs: 1.2, sm: 2 }, mb: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 900, mb: 1 }}>
          Indicador do ambiente por parâmetro
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          O score usa a <b>taxa de ocorrências</b> (ocorrências do parâmetro / amostras do parâmetro no período) e pondera pela <b>gravidade</b>.
        </Typography>

        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.2 }}>
          {indicadorPorParametro.length === 0 ? (
            <Chip label="Sem parâmetros ativos no período" variant="outlined" />
          ) : (
            indicadorPorParametro.map((it) => (
              <Paper
                key={it.key}
                sx={{
                  p: 1.2,
                  minWidth: isMobile ? "100%" : 260,
                  flex: "1 1 260px",
                  borderRadius: 2,
                  border: `1px solid ${it.statusColor}55`,
                }}
              >
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1 }}>
                  <Typography sx={{ fontWeight: 900, color: it.cor }}>{it.label}</Typography>
                  <Chip
                    size="small"
                    label={it.status}
                    sx={{ bgcolor: it.statusColor, color: "#fff", fontWeight: 900 }}
                  />
                </Box>

                <Box sx={{ mt: 0.7, display: "flex", flexWrap: "wrap", gap: 0.8, alignItems: "center" }}>
                  <Chip size="small" variant="outlined" label={`Score: ${it.score == null ? "-" : it.score.toFixed(0)}`} />
                  <Chip size="small" variant="outlined" label={`Ocorrências: ${it.alarms}`} />
                  <Chip size="small" variant="outlined" label={`Amostras: ${it.total}`} />
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`Taxa: ${it.ratePct == null ? "-" : it.ratePct.toFixed(2)}%`}
                  />
                </Box>

                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.7 }}>
                  Gravidade média: {it.alarms ? it.avgSev.toFixed(2) : "-"}
                </Typography>
              </Paper>
            ))
          )}
        </Box>
      </Paper>

      {/* GRÁFICOS */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: { xs: 2, sm: 3 },
          justifyContent: "center",
          mb: 3,
          width: "100%",
        }}
      >
        <Paper sx={{ p: 2, minWidth: { xs: 230, sm: 320 }, maxWidth: 560, flex: "1 1 320px" }}>
          <Typography variant="subtitle1" mb={2} sx={{ fontWeight: 900 }}>
            Ocorrências por Parâmetro ({modoGrafico === "rate" ? "Taxa (%)" : modoGrafico === "count" ? "Quantidade" : "Impacto"})
          </Typography>
          <Bar
            data={barrasData}
            options={{
              indexAxis: "y",
              plugins: { legend: { display: false } },
              scales: { x: { beginAtZero: true } },
            }}
            height={isMobile ? 160 : 230}
          />
        </Paper>

        <Paper sx={{ p: 2, minWidth: { xs: 230, sm: 320 }, maxWidth: 560, flex: "1 1 320px" }}>
          <Typography variant="subtitle1" mb={2} sx={{ fontWeight: 900 }}>
            Evolução ({modoGrafico === "rate" ? "Taxa média (%)" : modoGrafico === "count" ? "Quantidade" : "Impacto"})
          </Typography>
          <Line
            data={lineData}
            options={{
              plugins: { legend: { display: false } },
              scales: { x: { title: { display: true, text: "Dia" } } },
            }}
            height={isMobile ? 150 : 230}
          />
        </Paper>

        <Paper sx={{ p: 2, minWidth: { xs: 220, sm: 260 }, maxWidth: 360, flex: "1 1 220px" }}>
          <Typography variant="subtitle1" mb={2} sx={{ fontWeight: 900 }}>
            Proporção ({modoGrafico === "rate" ? "Taxa (%)" : modoGrafico === "count" ? "Quantidade" : "Impacto"})
          </Typography>
          <Doughnut data={pizzaData} options={{ plugins: { legend: { position: "bottom" } } }} height={isMobile ? 120 : 170} />
        </Paper>
      </Box>

      {erro && <Alert severity="error">{erro}</Alert>}

      <Paper sx={{ p: { xs: 1.2, sm: 2 }, mb: 3, overflowX: "auto" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
          <Typography variant="subtitle1">
            {tableRows.length === 0
              ? "Nenhuma ocorrência encontrada no período/filtro selecionado."
              : `${tableRows.length} ${groupByHour ? "linhas (resumo por hora)" : "eventos"} no período:`}
          </Typography>

          <Chip
            size="small"
            label={groupByHour ? "Modo resumo (agrupado)" : "Modo detalhado (mais pesado)"}
            color={groupByHour ? "success" : "warning"}
            variant="outlined"
          />
        </Box>

        <TableContainer sx={{ mt: 1.5 }}>
          <Table size="small" sx={{ minWidth: 900 }} stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell width={140}>{groupByHour ? "Hora" : "Horário"}</TableCell>
                <TableCell>Parâmetro</TableCell>
                <TableCell width={150}>{groupByHour ? "Pior faixa" : "Faixa"}</TableCell>
                <TableCell width={170}>{groupByHour ? "Máx / Média" : "Valor"}</TableCell>
                <TableCell width={160}>Critério</TableCell>
                <TableCell width={190}>
                  <Tooltip title={groupByHour ? "Ocorrências na hora + Δ do pico vs critério (e Δ da média)" : "Δ do evento vs critério"}>
                    <span>{groupByHour ? "Ocorrências / Δ" : "Δ (%)"}</span>
                  </Tooltip>
                </TableCell>
                <TableCell width={180}>Referência</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {pagedRows.map((row, idx) => {
                if (groupByHour) {
                  const avg = row.count ? row.sum / row.count : 0;

                  const hasRange = Number.isFinite(row.minAllowed) && Number.isFinite(row.maxAllowed);
                  const dMax = hasRange
                    ? deltaPctAgainstRange(row.max, row.minAllowed, row.maxAllowed)
                    : deltaPctAgainstLimit(row.max, Number(row.limite));

                  const dAvg = hasRange
                    ? deltaPctAgainstRange(avg, row.minAllowed, row.maxAllowed)
                    : deltaPctAgainstLimit(avg, Number(row.limite));

                  const crit = limitLabel(row);

                  return (
                    <TableRow key={`${row.bucket}_${row.key}_${idx}`} sx={{ bgcolor: (row.corBase || "#999") + "18" }}>
                      <TableCell>
                        <Chip label={row.bucket} size="small" color="warning" variant="outlined" />
                      </TableCell>

                      <TableCell>
                        <b>{row.label}</b>
                      </TableCell>

                      <TableCell>
                        <Chip label={row.worstFaixa} size="small" sx={{ bgcolor: row.corBase, color: "#fff", fontWeight: 700 }} />
                      </TableCell>

                      <TableCell>
                        <Tooltip title="Máximo / Média dentro do intervalo de 1 hora">
                          <span style={{ fontWeight: 800, color: row.corBase }}>
                            {row.max.toFixed(2)} / {avg.toFixed(2)} {row.unidade}
                          </span>
                        </Tooltip>
                      </TableCell>

                      <TableCell>
                        <span style={{ color: "#888" }}>{crit}</span>
                      </TableCell>

                      <TableCell>
                        <Chip
                          size="small"
                          label={`${row.count}x`}
                          sx={{ fontWeight: 900 }}
                          color={row.count >= 10 ? "error" : row.count >= 5 ? "warning" : "default"}
                          variant={row.count >= 5 ? "filled" : "outlined"}
                        />

                        {dMax != null && Number.isFinite(dMax) && (
                          <Tooltip title="Δ do pico (máximo) vs critério">
                            <Typography
                              component="span"
                              sx={{
                                ml: 1,
                                fontWeight: 900,
                                color: dMax > 0 ? "#d32f2f" : "#1976d2",
                              }}
                            >
                              {dMax > 0 ? "▲" : "▼"} {dMax.toFixed(1)}%
                            </Typography>
                          </Tooltip>
                        )}

                        {dAvg != null && Number.isFinite(dAvg) && (
                          <Tooltip title="Δ da média vs critério">
                            <Typography component="span" sx={{ ml: 1, color: "#777", fontWeight: 700 }}>
                              (média {dAvg > 0 ? "▲" : "▼"} {dAvg.toFixed(1)}%)
                            </Typography>
                          </Tooltip>
                        )}
                      </TableCell>

                      <TableCell>
                        <Typography variant="caption">{row.fonte || "-"}</Typography>
                      </TableCell>
                    </TableRow>
                  );
                }

                // detalhado (evento a evento)
                const al = row;
                const hasRange = Number.isFinite(al.min) && Number.isFinite(al.max);
                const d = hasRange
                  ? deltaPctAgainstRange(Number(al.valor), al.min, al.max)
                  : deltaPctAgainstLimit(Number(al.valor), Number(al.limite));

                return (
                  <TableRow key={`${al.ts}_${al.key}_${idx}`} sx={{ bgcolor: (al.cor || "#999") + "18" }}>
                    <TableCell>
                      <Chip label={al.horario} size="small" color="warning" variant="outlined" />
                    </TableCell>

                    <TableCell>
                      <b>{al.label}</b>
                    </TableCell>

                    <TableCell>
                      <Chip label={al.faixa} size="small" sx={{ bgcolor: al.cor, color: "#fff", fontWeight: 700 }} />
                    </TableCell>

                    <TableCell>
                      <span style={{ color: al.cor, fontWeight: 800 }}>
                        {Number(al.valor).toFixed(2)} {al.unidade}
                      </span>
                    </TableCell>

                    <TableCell>
                      <span style={{ color: "#888" }}>{limitLabel(al)}</span>
                    </TableCell>

                    <TableCell>
                      <b style={{ color: d != null && d > 0 ? "#d32f2f" : "#1976d2" }}>
                        {d == null ? "-" : `${d > 0 ? "▲" : "▼"} ${d.toFixed(1)}%`}
                      </b>
                    </TableCell>

                    <TableCell>
                      <Typography variant="caption">{al.fonte || "-"}</Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={tableRows.length}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 15, 25, 50]}
          labelRowsPerPage="Linhas por página"
        />
      </Paper>
    </Box>
  );
}
